import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Clock, BookOpen, Award, ArrowUpRight, Minus, Plus, Users } from "lucide-react";
import PayPalButton from "@/components/PayPalButton";

export default function CourseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [enrolled, setEnrolled] = useState(false);
  const [mode, setMode] = useState("personal"); // personal | license
  const [seats, setSeats] = useState(10);
  const [showPay, setShowPay] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/courses/${id}`);
        setCourse(r.data);
        const enrs = await api.get("/enrollments/mine");
        setEnrolled(enrs.data.some(e => e.course_id === id));
      } catch {
        toast.error("Course not found");
      }
    })();
  }, [id]);

  useEffect(() => {
    if (user?.role === "org_admin") setMode("license");
  }, [user]);

  const enrollFree = async () => {
    try {
      await api.post("/enrollments", { course_id: id });
      toast.success("Enrolled!");
      nav(`/learn/${id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not enroll");
    }
  };

  const onPaid = async (data) => {
    setShowPay(false);
    if (data?.license) {
      toast.success("Seats purchased — share the join link with your team");
      nav("/licenses");
    } else {
      toast.success("Payment received");
      nav(`/learn/${id}`);
    }
  };

  if (!course) {
    return <Layout><div className="px-10 py-20">Loading…</div></Layout>;
  }

  const totalLessons = course.modules?.reduce((s, m) => s + (m.lessons?.length || 0), 0) || 0;
  const totalMin = course.modules?.reduce((s, m) => s + (m.lessons?.reduce((a, l) => a + (l.duration_min || 0), 0) || 0), 0) || 0;
  const isPaid = course.price > 0;
  const isOrgAdmin = user?.role === "org_admin";
  const isLearner = user?.role === "learner";
  const totalPrice = (course.price || 0) * (mode === "license" ? Math.max(1, seats) : 1);

  return (
    <Layout>
      <div className="px-10 py-10 max-w-6xl">
        <Link to="/marketplace" className="overline hover:text-[#E63946]">← Back to marketplace</Link>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-6">
          <div className="lg:col-span-7">
            <div className="overline mb-4">{course.category}</div>
            <h1 className="editorial-h1 text-5xl mb-6">{course.title}</h1>
            <p className="text-lg text-[#52525B] leading-relaxed mb-8">{course.description}</p>
            <div className="flex items-center gap-6 mb-10 overline">
              <span className="flex items-center gap-2"><BookOpen size={14} strokeWidth={1.5}/> {totalLessons} lessons</span>
              <span className="flex items-center gap-2"><Clock size={14} strokeWidth={1.5}/> {totalMin} min</span>
              <span className="flex items-center gap-2"><Award size={14} strokeWidth={1.5}/> Certificate</span>
            </div>

            <h2 className="editorial-h2 text-3xl mb-4">Curriculum</h2>
            <div className="space-y-3">
              {course.modules?.map((m, i) => (
                <div key={m.id} className="editorial-card-static p-5" data-testid={`module-${i}`}>
                  <div className="overline mb-1">Module {i+1}</div>
                  <div className="font-serif text-xl mb-3">{m.title}</div>
                  <ul className="text-sm space-y-1.5 text-[#52525B]">
                    {m.lessons?.map(l => (
                      <li key={l.id} className="flex items-center justify-between">
                        <span>· {l.title}</span>
                        <span className="overline">{l.duration_min}m</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="sticky top-24">
              <img src={course.thumbnail} alt="" className="w-full h-64 object-cover border border-[#0A0A0A] mb-6" />
              <div className="editorial-card-static p-6">
                {/* Mode tabs for org_admin */}
                {isPaid && isOrgAdmin && (
                  <div className="flex gap-2 mb-5 border-b border-[#E4E4E7]">
                    <button onClick={() => setMode("license")} data-testid="mode-license" className={`px-4 py-2 text-sm font-display border-b-2 ${mode === "license" ? "border-[#E63946]" : "border-transparent text-[#52525B]"}`}>For my team</button>
                    <button onClick={() => setMode("personal")} data-testid="mode-personal" className={`px-4 py-2 text-sm font-display border-b-2 ${mode === "personal" ? "border-[#E63946]" : "border-transparent text-[#52525B]"}`}>Just me</button>
                  </div>
                )}

                {mode === "license" && isPaid && isOrgAdmin && (
                  <div className="mb-5">
                    <label className="overline block mb-2">Seats</label>
                    <div className="flex items-center gap-3 mb-2">
                      <button onClick={() => setSeats(Math.max(1, seats - 5))} className="p-2 border border-[#E4E4E7] hover:bg-[#F9F9F8]"><Minus size={14}/></button>
                      <input type="number" min={1} value={seats} onChange={(e) => setSeats(Math.max(1, parseInt(e.target.value || "1")))} data-testid="seats-input" className="w-20 text-center px-3 py-2 border border-[#E4E4E7] font-display text-lg" />
                      <button onClick={() => setSeats(seats + 5)} className="p-2 border border-[#E4E4E7] hover:bg-[#F9F9F8]"><Plus size={14}/></button>
                      <span className="overline text-[#52525B]">${course.price}/seat</span>
                    </div>
                  </div>
                )}

                <div className="flex items-baseline gap-2 mb-6">
                  <div className="font-serif text-5xl font-light" data-testid="price-display">{course.price > 0 ? `$${totalPrice.toFixed(2)}` : "Free"}</div>
                  {course.price > 0 && <div className="overline">{course.currency}</div>}
                </div>

                {enrolled && mode === "personal" ? (
                  <Link to={`/learn/${id}`} data-testid="continue-course" className="btn-primary w-full justify-center">
                    Continue learning <ArrowUpRight size={16} strokeWidth={1.5}/>
                  </Link>
                ) : !isPaid ? (
                  <button onClick={enrollFree} data-testid="enroll-free" className="btn-primary w-full justify-center">
                    Enroll free <ArrowUpRight size={16} strokeWidth={1.5}/>
                  </button>
                ) : isLearner ? (
                  <button onClick={() => setShowPay(true)} data-testid="enroll-button" className="btn-primary w-full justify-center">
                    Buy & enroll <ArrowUpRight size={16} strokeWidth={1.5}/>
                  </button>
                ) : (
                  <button onClick={() => setShowPay(true)} data-testid="buy-seats" className="btn-primary w-full justify-center">
                    {mode === "license" ? <><Users size={14}/> Buy {seats} seats</> : <>Buy for myself <ArrowUpRight size={16} strokeWidth={1.5}/></>}
                  </button>
                )}

                <div className="overline mt-4 text-center">Lifetime access · Certificate · AI Coach</div>

                {showPay && (
                  <div className="mt-6 border-t border-[#E4E4E7] pt-6">
                    <div className="overline mb-3">Pay with PayPal</div>
                    <PayPalButton courseId={id} seats={mode === "license" ? seats : 1} purchaseAs={mode === "license" ? "license" : "personal"} onPaid={onPaid} />
                  </div>
                )}
              </div>
              {mode === "license" && isOrgAdmin && (
                <div className="mt-4 editorial-card-static p-4 text-sm text-[#52525B]">
                  After purchase, you'll get a shareable join link. Send it to your team — they self-enroll, you track completion + scores in <span className="font-display text-[#0A0A0A]">Analytics</span>.
                </div>
              )}
              <div className="mt-6 text-sm text-[#52525B]">Created by <span className="font-display text-[#0A0A0A]">{course.created_by_name}</span></div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
